import { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import type { User, CreateUserRequest, Establishment } from '../types';

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, estData] = await Promise.all([
        api.getUsers(),
        api.getEstablishments(),
      ]);
      setUsers(usersData);
      setEstablishments(estData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = loadData;

  const handleDelete = async (userId: number) => {
    if (!confirm('Удалить сотрудника?')) return;
    
    try {
      await api.deleteUser(userId);
      loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  // Filter users based on role
  const filteredUsers = user?.role === 'manager' 
    ? users.filter(u => u.manager_id === user.id || u.id === user.id)
    : users;

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>Команда</h2>
        {user?.role !== 'employee' && (
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + Добавить
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="user-list">
        {filteredUsers.map((u) => (
          <div key={u.id} className="user-item">
            <div className="user-info">
              <span className="user-name">
                {u.first_name} {u.last_name || ''}
                {u.username && <span style={{ color: 'var(--gray-500)' }}> @{u.username}</span>}
              </span>
              <span className="user-role">
                <span className={`badge badge-${u.role}`}>{getRoleText(u.role)}</span>
              </span>
            </div>
            {user?.role === 'admin' && (
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button
                  className="btn btn-secondary"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                  onClick={() => setEditingUser(u)}
                >
                  Изменить
                </button>
                <button
                  className="btn btn-danger"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                  onClick={() => handleDelete(u.id)}
                >
                  Удалить
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreated={loadUsers}
          currentUser={user}
          establishments={establishments}
          managers={users.filter(u => u.role === 'manager' || u.role === 'admin')}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onUpdated={loadUsers}
          establishments={establishments}
          managers={users.filter(u => (u.role === 'manager' || u.role === 'admin') && u.id !== editingUser.id)}
        />
      )}
    </div>
  );
}

function getRoleText(role: string): string {
  const map: Record<string, string> = {
    admin: 'Админ',
    manager: 'Менеджер',
    employee: 'Сотрудник',
  };
  return map[role] || role;
}

function CreateUserModal({ onClose, onCreated, currentUser, establishments: establishmentsProp, managers }: {
  onClose: () => void;
  onCreated: () => void;
  currentUser: User | null;
  establishments: Establishment[];
  managers: User[];
}) {
  const [formData, setFormData] = useState<CreateUserRequest>({
    first_name: '',
    role: 'employee',
    manager_id: currentUser?.role === 'manager' ? currentUser.id : undefined,
    establishment_id: currentUser?.establishment_id || undefined,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [telegramCache, setTelegramCache] = useState<{ chat_id: number; username: string | null; first_name: string | null }[]>([]);
  const [showTelegramUsers, setShowTelegramUsers] = useState(false);

  // Use establishments and managers from props
  const establishments = establishmentsProp;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const telegramRes = currentUser?.role === 'admin' ? await api.getTelegramCache() : [];
      if (currentUser?.role === 'admin') {
        setTelegramCache(telegramRes);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const selectTelegramUser = (telegramUser: { chat_id: number; username: string | null; first_name: string | null }) => {
    setFormData({
      ...formData,
      telegram_chat_id: telegramUser.chat_id,
      first_name: telegramUser.first_name || formData.first_name,
      username: telegramUser.username || undefined,
    });
    setShowTelegramUsers(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name) return;

    setLoading(true);
    setError(null);

    try {
      await api.createUser(formData);
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Добавить сотрудника</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {currentUser?.role === 'admin' && (
          <div style={{ marginBottom: '1rem' }}>
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => setShowTelegramUsers(!showTelegramUsers)}
              style={{ width: '100%' }}
            >
              {showTelegramUsers ? 'Скрыть' : 'Выбрать из Telegram кэша'}
            </button>
            {showTelegramUsers && (
              <div className="checkbox-group" style={{ marginTop: '0.5rem', maxHeight: '150px' }}>
                {telegramCache.map((u) => (
                  <div 
                    key={u.chat_id} 
                    className="checkbox-item"
                    onClick={() => selectTelegramUser(u)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span>{u.first_name || 'Unknown'} (@{u.username || 'no username'}) - {u.chat_id}</span>
                  </div>
                ))}
                {telegramCache.length === 0 && <p style={{ padding: '0.5rem', color: 'var(--gray-500)' }}>Нет данных</p>}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Telegram Chat ID</label>
            <input
              type="number"
              className="form-input"
              value={formData.telegram_chat_id || ''}
              onChange={(e) => setFormData({ ...formData, telegram_chat_id: parseInt(e.target.value) || undefined })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Имя *</label>
            <input
              type="text"
              className="form-input"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Фамилия</label>
            <input
              type="text"
              className="form-input"
              value={formData.last_name || ''}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value || undefined })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Telegram username</label>
            <input
              type="text"
              className="form-input"
              value={formData.username || ''}
              onChange={(e) => setFormData({ ...formData, username: e.target.value || undefined })}
              placeholder="@username"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Роль *</label>
            <select
              className="form-select"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'manager' | 'employee' })}
            >
              <option value="employee">Сотрудник</option>
              <option value="manager">Менеджер</option>
              <option value="admin">Админ</option>
            </select>
          </div>

          {currentUser?.role === 'admin' && (
            <>
              <div className="form-group">
                <label className="form-label">Менеджер</label>
                <select
                  className="form-select"
                  value={formData.manager_id || ''}
                  onChange={(e) => setFormData({ ...formData, manager_id: parseInt(e.target.value) || undefined })}
                >
                  <option value="">Не выбран</option>
                  {managers.map((m) => (
                    <option key={m.id} value={m.id}>{m.first_name} {m.last_name || ''}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Заведение</label>
                <select
                  className="form-select"
                  value={formData.establishment_id || ''}
                  onChange={(e) => setFormData({ ...formData, establishment_id: parseInt(e.target.value) || undefined })}
                >
                  <option value="">Не выбрано</option>
                  {establishments.map((est) => (
                    <option key={est.id} value={est.id}>{est.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <button type="submit" className="btn btn-primary btn-full" disabled={loading || !formData.first_name}>
            {loading ? 'Создание...' : 'Создать'}
          </button>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onUpdated, establishments: establishmentsProp, managers }: {
  user: User;
  onClose: () => void;
  onUpdated: () => void;
  establishments: Establishment[];
  managers: User[];
}) {
  const [formData, setFormData] = useState<Partial<CreateUserRequest>>({
    first_name: user.first_name,
    last_name: user.last_name || undefined,
    username: user.username || undefined,
    role: user.role,
    manager_id: user.manager_id || undefined,
    establishment_id: user.establishment_id || undefined,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const establishments = establishmentsProp;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name) return;

    setLoading(true);
    setError(null);

    try {
      await api.updateUser(user.id, formData);
      onUpdated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Изменить сотрудника</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Имя *</label>
            <input
              type="text"
              className="form-input"
              value={formData.first_name || ''}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Фамилия</label>
            <input
              type="text"
              className="form-input"
              value={formData.last_name || ''}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value || undefined })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Telegram username</label>
            <input
              type="text"
              className="form-input"
              value={formData.username || ''}
              onChange={(e) => setFormData({ ...formData, username: e.target.value || undefined })}
              placeholder="@username"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Роль *</label>
            <select
              className="form-select"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'manager' | 'employee' })}
            >
              <option value="employee">Сотрудник</option>
              <option value="manager">Менеджер</option>
              <option value="admin">Админ</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Менеджер</label>
            <select
              className="form-select"
              value={formData.manager_id || ''}
              onChange={(e) => setFormData({ ...formData, manager_id: parseInt(e.target.value) || undefined })}
            >
              <option value="">Не выбран</option>
              {managers.map((m) => (
                <option key={m.id} value={m.id}>{m.first_name} {m.last_name || ''}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Заведение</label>
            <select
              className="form-select"
              value={formData.establishment_id || ''}
              onChange={(e) => setFormData({ ...formData, establishment_id: parseInt(e.target.value) || undefined })}
            >
              <option value="">Не выбрано</option>
              {establishments.map((est) => (
                <option key={est.id} value={est.id}>{est.name}</option>
              ))}
            </select>
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading || !formData.first_name}>
            {loading ? 'Сохранение...' : 'Сохранить'}
          </button>
        </form>
      </div>
    </div>
  );
}
